import { Client, StompHeaders } from '@stomp/stompjs'
import { useAuth } from '@/auth'
import useNotifications, { isVisibleNotification } from '@/composables/useNotifications'
import { SaiftyNotification } from '@/config'
import { h, nextTick, ref, watch } from 'vue'
import useToasts from '@/composables/useToasts'
import Notification from '@/components/Notification.vue'
import { debounceFilter, useDebounceFn, useDocumentVisibility, watchWithFilter } from '@vueuse/core'
import { notificationsApi } from '@/api'
import { NotificationRequestStatusEnum } from '@coac-gmbh/notification-api'

const DEFAULT_NOTIFICATION_TIMEOUT = 5000
const MAX_RECONNECTION_ATTEMPTS = 5
const stompClient = ref<Client>()
const reconnectionAttempts = ref(0)

export function closeSocketConnection() {
  if (stompClient.value) {
    stompClient.value.deactivate()
  }
  reconnectionAttempts.value = 0
}

export const ARE_NOTIFICATIONS_ENABLED =
  !!import.meta.env.VITE_NOTIFICATIONS_WS_HOST ||
  (window.ENV.NOTIFICATIONS_WS_HOST && window.ENV.NOTIFICATIONS_WS_HOST !== '**NOTIFICATIONS_WS_HOST**')

export default function useSockets() {
  const visibility = useDocumentVisibility()
  const { notifications, notificationsNotRead, technicalNotificationsNotRead } = useNotifications()
  const notificationQueue = ref<SaiftyNotification[]>([])
  const auth = useAuth()
  const { addToast } = useToasts()
  const token = ref<string>('')
  const headers = ref<StompHeaders>()

  watch(token, (newValue) => {
    headers.value = {
      authorization: `Bearer ${newValue}`,
    }
  })

  watch(notificationsNotRead, () => {
    if (notificationsNotRead.value.length && ARE_NOTIFICATIONS_ENABLED) {
      debouncedNotificationsShowedFn()
    }
  })

  watchWithFilter(
    [notificationQueue, visibility],
    () => {
      if (notificationQueue.value.length && visibility.value === 'visible') {
        showNotificationsInQueue()
      }
    },
    {
      eventFilter: debounceFilter(500, { maxWait: 1000 }),
    }
  )

  const debouncedNotificationsShowedFn = useDebounceFn(() => {
    notificationsRead()
  }, 500)

  async function createClient() {
    if (!token.value) {
      token.value = await auth.getAccessToken()
      await nextTick()
    }

    const client = new Client({
      brokerURL: `wss://${
        import.meta.env.VITE_NOTIFICATIONS_WS_HOST || window.ENV.NOTIFICATIONS_WS_HOST
      }/ws?authorization=${token.value}`,
      connectHeaders: headers.value,
      reconnectDelay: DEFAULT_NOTIFICATION_TIMEOUT,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
    })

    client.onConnect = () => {
      console.log('Connected to STOMP')
      reconnectionAttempts.value = 0 // Reset attempts on successful connection
      subscribeToNotifications()
    }

    const handleReconnection = async () => {
      reconnectionAttempts.value++
      if (reconnectionAttempts.value <= MAX_RECONNECTION_ATTEMPTS) {
        console.log(`Attempting to reconnect (attempt ${reconnectionAttempts.value}/${MAX_RECONNECTION_ATTEMPTS})...`)
      }

      if (reconnectionAttempts.value > MAX_RECONNECTION_ATTEMPTS) {
        console.log('Max reconnection attempts reached')
        client.forceDisconnect()
        client.deactivate()
      }
    }

    client.onWebSocketClose = handleReconnection
    client.onWebSocketError = handleReconnection

    return client
  }

  async function createSocket() {
    stompClient.value = await createClient()
    await nextTick()
    stompClient.value.activate()
  }

  function subscribeToNotifications() {
    if (stompClient.value?.connected) {
      const subscriptionPath = `/user/queue/notify`

      stompClient.value.subscribe(
        subscriptionPath,
        (message: { body: string }) => {
          const payloadNotification = JSON.parse(message.body) as SaiftyNotification

          const notificationFound = notifications.value.find(
            (n) => payloadNotification.messageId && n.messageId === payloadNotification.messageId
          )

          if (!notificationFound) {
            insertNotificationOrdered(payloadNotification)
            if (!payloadNotification.status && ARE_NOTIFICATIONS_ENABLED) {
              notificationQueue.value = [...notificationQueue.value, payloadNotification]
            }
          }
        },
        headers.value
      )
    }
  }

  async function runSocket() {
    console.log('Creating socket connection...')
    if (!stompClient.value) {
      await createSocket()
    } else if (!stompClient.value.connected) {
      try {
        stompClient.value.activate()
      } catch (error) {
        console.error('Error activating socket:', error)
        stompClient.value = undefined
        await createSocket()
      }
    }
  }

  function insertNotificationOrdered(notification: SaiftyNotification) {
    const index = notifications.value.findIndex((n) => n.timestamp < notification.timestamp)
    if (index === -1) {
      notifications.value = [...notifications.value, notification]
    } else {
      notifications.value.splice(index, 0, notification)
    }
  }

  async function notificationsRead() {
    const notificationsToMarkAsRead = [...notificationsNotRead.value, ...technicalNotificationsNotRead.value]
    console.log(`Marking ${notificationsToMarkAsRead.length} notifications as read`)
    const notificationPromises: Promise<any>[] = []
    notificationsToMarkAsRead.forEach((notification) => {
      notificationPromises.push(
        new Promise((resolve) => {
          notificationsApi.updateStatus({
            notificationRequest: {
              messageId: notification.messageId,
              status: NotificationRequestStatusEnum.Read,
            },
          })
          resolve(null)
        })
      )
    })
    // Update the local variables to avoid repeating unnecessary requests
    notifications.value.forEach((notification) => {
      if (!notification.status) {
        notification.status = NotificationRequestStatusEnum.Read
      }
    })

    await Promise.all(notificationPromises)
  }

  function showNotificationsInQueue() {
    notificationQueue.value.forEach((payloadNotification) => {
      if (isVisibleNotification(payloadNotification)) {
        addToast({
          variant: 'notification',
          autohide: false,
          customVNode: h(Notification, { data: payloadNotification }),
        })
      }
    })
    notificationQueue.value = []
  }

  return {
    stompClient,
    runSocket,
    notificationsRead,
    closeSocketConnection,
  }
}
